import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, Briefcase, Mail, Search, MoreVertical, Shield, Eye, Trash2, CheckCircle, XCircle, Loader2, ClipboardList, TrendingUp, PieChart, HelpCircle, MessageSquare, ArrowUpDown, ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import LiquidityDashboard from '../components/LiquidityDashboard';
import { useAuth, API } from '../App';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

export default function AdminDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [providers, setProviders] = useState([]);
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [onboardings, setOnboardings] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportStats, setSupportStats] = useState({ total: 0, new_count: 0 });
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state for each table
  const [userSort, setUserSort] = useState({ field: 'created_at', direction: 'desc' });
  const [dealSort, setDealSort] = useState({ field: 'created_at', direction: 'desc' });
  const [providerSort, setProviderSort] = useState({ field: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, dealsRes, providersRes, onbStatsRes, onbListRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/deals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/providers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/onboarding/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/onboarding`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { onboardings: [] } })),
        axios.get(`${API}/admin/support`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { messages: [], total: 0, new_count: 0 } }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setDeals(dealsRes.data.deals);
      setProviders(providersRes.data.providers);
      setOnboardingStats(onbStatsRes.data);
      setOnboardings(onbListRes.data.onboardings || []);
      setSupportMessages(supportRes.data.messages || []);
      setSupportStats({ total: supportRes.data.total || 0, new_count: supportRes.data.new_count || 0 });
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateDealStatus = async (dealId, status) => {
    try {
      await axios.put(`${API}/admin/deals/${dealId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(prev => prev.map(d => d.deal_id === dealId ? { ...d, status } : d));
      toast.success('Deal status updated');
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  const handleUpdateProviderStatus = async (providerId, status) => {
    try {
      await axios.put(`${API}/admin/providers/${providerId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(prev => prev.map(p => p.provider_id === providerId ? { ...p, status } : p));
      toast.success('Provider status updated');
    } catch (error) {
      toast.error('Failed to update provider');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleVerified = async (userId, verified) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/verified?verified=${verified}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, verified } : u));
      toast.success(verified ? 'User verified' : 'Verification removed');
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleUpdateSupportStatus = async (supportId, status) => {
    try {
      await axios.put(`${API}/admin/support/${supportId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportMessages(prev => prev.map(m => m.support_id === supportId ? { ...m, status } : m));
      if (status !== 'new') {
        setSupportStats(prev => ({ ...prev, new_count: Math.max(0, prev.new_count - 1) }));
      }
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Generic sort function
  const sortData = (data, sortConfig) => {
    if (!sortConfig.field) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];
      
      // Handle nested values or special cases
      if (sortConfig.field === 'name' && !aVal) aVal = a.business_name || '';
      if (sortConfig.field === 'name' && !bVal) bVal = b.business_name || '';
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle dates
      if (sortConfig.field === 'created_at' || sortConfig.field === 'updated_at') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      const comparison = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  };

  // Toggle sort direction or set new field
  const handleSort = (setter, currentSort, field) => {
    if (currentSort.field === field) {
      setter({ field, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setter({ field, direction: 'asc' });
    }
  };

  // Sortable column header component
  const SortHeader = ({ label, field, sortConfig, onSort }) => (
    <th 
      className="cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.field === field ? (
          sortConfig.direction === 'asc' ? 
            <ArrowUp className="h-4 w-4 text-blue-600" /> : 
            <ArrowDown className="h-4 w-4 text-blue-600" />
        ) : (
          <ArrowUpDown className="h-4 w-4 text-slate-400" />
        )}
      </div>
    </th>
  );

  // Sorted and filtered data
  const sortedUsers = useMemo(() => sortData(users, userSort), [users, userSort]);
  const sortedDeals = useMemo(() => sortData(deals, dealSort), [deals, dealSort]);
  const sortedProviders = useMemo(() => sortData(providers, providerSort), [providers, providerSort]);

  const filteredUsers = sortedUsers.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredDeals = sortedDeals.filter(d =>
    d.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredProviders = sortedProviders.filter(p =>
    p.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="admin-title">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">Manage users, deals, and providers</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</div>
                <div className="text-sm text-slate-500">Total Users</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats?.active_deals || 0}</div>
                <div className="text-sm text-slate-500">Active Deals</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats?.active_providers || 0}</div>
                <div className="text-sm text-slate-500">Providers</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats?.unread_inquiries || 0}</div>
                <div className="text-sm text-slate-500">Unread Inquiries</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="liquidity" data-testid="admin-tab-liquidity">Liquidity</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
              <TabsTrigger value="deals" data-testid="admin-tab-deals">Deals</TabsTrigger>
              <TabsTrigger value="providers" data-testid="admin-tab-providers">Providers</TabsTrigger>
              <TabsTrigger value="onboarding" data-testid="admin-tab-onboarding">Onboarding</TabsTrigger>
              <TabsTrigger value="support" data-testid="admin-tab-support" className="relative">
                Support
                {supportStats.new_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {supportStats.new_count}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            {activeTab !== 'overview' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="admin-search"
                />
              </div>
            )}
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Users */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Recent Users</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('users')}>View All</Button>
                </div>
                <div className="space-y-3">
                  {users.slice(0, 5).map(user => (
                    <div key={user.user_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 font-medium">{user.name?.charAt(0) || 'U'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{user.name}</div>
                        <div className="text-sm text-slate-500 truncate">{user.email}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        user.role === 'provider' ? 'bg-green-100 text-green-700' :
                        user.role === 'wholesaler' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Deals */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Recent Deals</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('deals')}>View All</Button>
                </div>
                <div className="space-y-3">
                  {deals.slice(0, 5).map(deal => (
                    <div key={deal.deal_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-12 h-12 bg-slate-200 rounded-lg overflow-hidden">
                        <img src={deal.photos?.[0] || 'https://via.placeholder.com/48'} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{deal.title}</div>
                        <div className="text-sm text-slate-500">{formatPrice(deal.asking_price)}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${deal.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                        {deal.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Liquidity Tab */}
          <TabsContent value="liquidity">
            <LiquidityDashboard token={token} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortHeader label="User" field="name" sortConfig={userSort} onSort={(f) => handleSort(setUserSort, userSort, f)} />
                      <SortHeader label="Email" field="email" sortConfig={userSort} onSort={(f) => handleSort(setUserSort, userSort, f)} />
                      <SortHeader label="Role" field="role" sortConfig={userSort} onSort={(f) => handleSort(setUserSort, userSort, f)} />
                      <SortHeader label="Joined" field="created_at" sortConfig={userSort} onSort={(f) => handleSort(setUserSort, userSort, f)} />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-700 font-medium text-sm">{user.name?.charAt(0) || 'U'}</span>
                            </div>
                            <span className="font-medium text-slate-900">{user.name}</span>
                          </div>
                        </td>
                        <td className="text-slate-600">{user.email}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                              user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              user.role === 'provider' ? 'bg-green-100 text-green-700' :
                              user.role === 'wholesaler' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {user.role}
                            </span>
                            {user.verified && (
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1" title="Verified user">
                                <CheckCircle className="h-3 w-3" /> Verified
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-slate-600 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleUpdateUserRole(user.user_id, 'investor')}>
                                Set as Investor
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateUserRole(user.user_id, 'wholesaler')}>
                                Set as Wholesaler
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateUserRole(user.user_id, 'provider')}>
                                Set as Provider
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateUserRole(user.user_id, 'admin')}>
                                Set as Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleVerified(user.user_id, !user.verified)}
                                data-testid={`toggle-verified-${user.user_id}`}
                              >
                                {user.verified ? 'Un-verify user' : 'Mark as Verified'}
                              </DropdownMenuItem>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600">
                                    Delete User
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete this user and all their data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteUser(user.user_id)} className="bg-red-500 hover:bg-red-600">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortHeader label="Property" field="title" sortConfig={dealSort} onSort={(f) => handleSort(setDealSort, dealSort, f)} />
                      <SortHeader label="Location" field="city" sortConfig={dealSort} onSort={(f) => handleSort(setDealSort, dealSort, f)} />
                      <SortHeader label="Price" field="asking_price" sortConfig={dealSort} onSort={(f) => handleSort(setDealSort, dealSort, f)} />
                      <SortHeader label="Status" field="status" sortConfig={dealSort} onSort={(f) => handleSort(setDealSort, dealSort, f)} />
                      <SortHeader label="Posted" field="created_at" sortConfig={dealSort} onSort={(f) => handleSort(setDealSort, dealSort, f)} />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeals.map(deal => (
                      <tr key={deal.deal_id} data-testid={`deal-row-${deal.deal_id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-slate-200 rounded-lg overflow-hidden">
                              <img src={deal.photos?.[0] || 'https://via.placeholder.com/48'} alt="" className="w-full h-full object-cover" />
                            </div>
                            <span className="font-medium text-slate-900 max-w-xs truncate">{deal.title}</span>
                          </div>
                        </td>
                        <td className="text-slate-600">{deal.city}, {deal.state}</td>
                        <td className="font-medium text-slate-900">{formatPrice(deal.asking_price)}</td>
                        <td>
                          <span className={`text-xs px-2 py-1 rounded-full ${deal.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                            {deal.status}
                          </span>
                        </td>
                        <td className="text-slate-600 text-sm">
                          {new Date(deal.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/deals/${deal.deal_id}`}>
                                  <Eye className="h-4 w-4 mr-2" /> View Deal
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/edit-deal/${deal.deal_id}`}>
                                  <Pencil className="h-4 w-4 mr-2" /> Edit Deal
                                </Link>
                              </DropdownMenuItem>
                              {deal.status === 'active' ? (
                                <DropdownMenuItem onClick={() => handleUpdateDealStatus(deal.deal_id, 'inactive')}>
                                  <XCircle className="h-4 w-4 mr-2" /> Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleUpdateDealStatus(deal.deal_id, 'active')}>
                                  <CheckCircle className="h-4 w-4 mr-2" /> Activate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortHeader label="Business" field="business_name" sortConfig={providerSort} onSort={(f) => handleSort(setProviderSort, providerSort, f)} />
                      <SortHeader label="Category" field="category" sortConfig={providerSort} onSort={(f) => handleSort(setProviderSort, providerSort, f)} />
                      <SortHeader label="Service Area" field="service_area" sortConfig={providerSort} onSort={(f) => handleSort(setProviderSort, providerSort, f)} />
                      <SortHeader label="Status" field="status" sortConfig={providerSort} onSort={(f) => handleSort(setProviderSort, providerSort, f)} />
                      <SortHeader label="Joined" field="created_at" sortConfig={providerSort} onSort={(f) => handleSort(setProviderSort, providerSort, f)} />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProviders.map(provider => (
                      <tr key={provider.provider_id} data-testid={`provider-row-${provider.provider_id}`}>
                        <td>
                          <span className="font-medium text-slate-900">{provider.business_name}</span>
                        </td>
                        <td className="text-slate-600">{provider.category}</td>
                        <td className="text-slate-600">{provider.service_area}</td>
                        <td>
                          <span className={`text-xs px-2 py-1 rounded-full ${provider.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                            {provider.status}
                          </span>
                        </td>
                        <td className="text-slate-600 text-sm">
                          {new Date(provider.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/providers/${provider.provider_id}`}>
                                  <Eye className="h-4 w-4 mr-2" /> View Profile
                                </Link>
                              </DropdownMenuItem>
                              {provider.status === 'active' ? (
                                <DropdownMenuItem onClick={() => handleUpdateProviderStatus(provider.provider_id, 'inactive')}>
                                  <XCircle className="h-4 w-4 mr-2" /> Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleUpdateProviderStatus(provider.provider_id, 'active')}>
                                  <CheckCircle className="h-4 w-4 mr-2" /> Activate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Onboarding Tab */}
          <TabsContent value="onboarding">
            <div className="space-y-6">
              {/* Onboarding Stats */}
              {onboardingStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                        <ClipboardList className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">{onboardingStats.onboarding_rate}%</div>
                        <div className="text-sm text-slate-500">Completion Rate</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">{onboardingStats.onboarded_users}</div>
                        <div className="text-sm text-slate-500">Onboarded Users</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">{onboardingStats.wants_discounts || 0}</div>
                        <div className="text-sm text-slate-500">Want Discounts</div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                        <PieChart className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">{onboardingStats.by_role?.investor || 0}</div>
                        <div className="text-sm text-slate-500">Investors</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Role Distribution */}
              {onboardingStats && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">By Role</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Investors</span>
                        <span className="font-semibold text-slate-900">{onboardingStats.by_role?.investor || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Wholesalers</span>
                        <span className="font-semibold text-slate-900">{onboardingStats.by_role?.wholesaler || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Providers</span>
                        <span className="font-semibold text-slate-900">{onboardingStats.by_role?.provider || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Top Services Needed</h3>
                    <div className="space-y-3">
                      {onboardingStats.top_services_needed?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-slate-600">{item.service}</span>
                          <span className="font-semibold text-slate-900">{item.count}</span>
                        </div>
                      ))}
                      {(!onboardingStats.top_services_needed || onboardingStats.top_services_needed.length === 0) && (
                        <p className="text-slate-500 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Price Range Distribution */}
              {onboardingStats && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Investor Price Ranges</h3>
                    <div className="space-y-3">
                      {onboardingStats.investor_price_range?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-slate-600">{item.range}</span>
                          <span className="font-semibold text-slate-900">{item.count}</span>
                        </div>
                      ))}
                      {(!onboardingStats.investor_price_range || onboardingStats.investor_price_range.length === 0) && (
                        <p className="text-slate-500 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Deals Per Year</h3>
                    <div className="space-y-3">
                      {onboardingStats.investor_deals_per_year?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-slate-600">{item.range} deals</span>
                          <span className="font-semibold text-slate-900">{item.count}</span>
                        </div>
                      ))}
                      {(!onboardingStats.investor_deals_per_year || onboardingStats.investor_deals_per_year.length === 0) && (
                        <p className="text-slate-500 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Onboarding List */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Recent Onboarding Responses</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Location</th>
                        <th>Key Data</th>
                        <th>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {onboardings.map(onb => (
                        <tr key={onb.onboarding_id} data-testid={`onboarding-row-${onb.onboarding_id}`}>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-700 font-medium text-sm">{onb.user?.name?.charAt(0) || 'U'}</span>
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">{onb.user?.name || 'Unknown'}</div>
                                <div className="text-sm text-slate-500">{onb.user?.email || ''}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                              onb.role === 'investor' ? 'bg-blue-100 text-blue-700' :
                              onb.role === 'wholesaler' ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {onb.role}
                            </span>
                          </td>
                          <td className="text-slate-600">
                            {onb.role === 'investor' && onb.investor ? (
                              <span>{onb.investor.based_city}, {onb.investor.based_state}</span>
                            ) : onb.role === 'wholesaler' && onb.wholesaler ? (
                              <span>{onb.wholesaler.based_city}, {onb.wholesaler.based_state}</span>
                            ) : onb.role === 'provider' && onb.provider?.markets_served?.length > 0 ? (
                              <span>{onb.provider.markets_served[0]?.value}</span>
                            ) : '-'}
                          </td>
                          <td className="text-slate-600 text-sm">
                            {onb.role === 'investor' && onb.investor ? (
                              <span>{onb.investor.purchase_price_range || '-'}</span>
                            ) : onb.role === 'wholesaler' && onb.wholesaler ? (
                              <span>{onb.wholesaler.deals_per_month || '-'} deals/mo</span>
                            ) : onb.role === 'provider' && onb.provider ? (
                              <span>{onb.provider.service_type || '-'}</span>
                            ) : '-'}
                          </td>
                          <td className="text-slate-600 text-sm">
                            {new Date(onb.completed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {onboardings.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-slate-500 py-8">
                            No onboarding data yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">Support Messages</h3>
                  <span className="text-sm text-slate-500">({supportStats.total} total, {supportStats.new_count} new)</span>
                </div>
              </div>
              
              <div className="divide-y divide-slate-100">
                {supportMessages.length === 0 ? (
                  <div className="p-12 text-center">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No support messages yet</p>
                  </div>
                ) : (
                  supportMessages.map(msg => (
                    <div key={msg.support_id} className={`p-4 ${msg.status === 'new' ? 'bg-blue-50' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-900">{msg.name}</span>
                            <span className="text-slate-400">•</span>
                            <a href={`mailto:${msg.email}`} className="text-blue-600 hover:underline text-sm">
                              {msg.email}
                            </a>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              msg.status === 'new' ? 'bg-blue-100 text-blue-700' :
                              msg.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                              msg.status === 'resolved' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {msg.status}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mb-2">
                            Subject: <span className="capitalize">{msg.subject.replace('_', ' ')}</span>
                            <span className="mx-2">•</span>
                            {new Date(msg.created_at).toLocaleString()}
                          </div>
                          <p className="text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateSupportStatus(msg.support_id, 'in_progress')}>
                              Mark In Progress
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateSupportStatus(msg.support_id, 'resolved')}>
                              Mark Resolved
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateSupportStatus(msg.support_id, 'closed')}>
                              Close
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={`mailto:${msg.email}?subject=Re: NextFlip Support - ${msg.subject}`}>
                                Reply via Email
                              </a>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
